import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import type { ChunkEvent } from "../sdk";
import { drawCellGrid, CELL_PENDING, CELL_DONE_NEUTRAL } from "./materialise-grid";

// The live "it's working" hero (utility-redesign plan §4.1 + §4.2). Two views over the
// streamed `onChunk` events, side by side:
//
//  • Materialise grid — a bespoke canvas of one cell per logical chunk, laid out in index
//    order, each cell filling as its chunk lands and COLOURED BY workerId. You literally
//    watch N workers tile the problem; more cores / mesh peers ⇒ it fills faster and in more
//    colours. (§4.2)
//  • Convergence chart (uPlot) — the running count-weighted mean of chunk samples vs.
//    iterations, with a ±1.96·SE band. This is explicitly a PREVIEW: it is built from chunks
//    in arrival order and is NOT the canonical result, which is reduced in fixed chunk-index
//    order by the SDK. On completion we snap the readout to the canonical result. (§4.1 + §2)
//
// Determinism note: nothing here feeds back into the computation. These are pure views over
// advisory `onChunk` events; the result the app records still comes from the Runner.

// The materialise grid (cells coloured by workerId) is the shared canvas in
// ./materialise-grid — the same component the #contribute page reuses (plan §4.2). The
// convergence chart below is submitter-only.

export interface RunVisualHandle {
  /** The streaming `onChunk` consumer to hand the Runner. */
  onChunk(c: ChunkEvent): void;
  /** Tear down the chart + observers (call when the panel is replaced). */
  dispose(): void;
}

/**
 * Create the live run visual inside `container`. Call `onChunk` for every streamed chunk; the
 * grid + convergence chart update incrementally. The grid is sized on the first event (which
 * carries `numChunks`), so the app never needs the chunk plan from past the seam.
 */
export function createRunVisual(
  container: HTMLElement,
  opts: { resultUnit: string; mode?: "local" | "mesh" },
): RunVisualHandle {
  const { resultUnit } = opts;
  const mode = opts.mode ?? "local";
  // Be explicit about WHAT the cells/counts represent so a local run (your CPU cores) isn't
  // mistaken for a mesh run (other people's browser tabs). This was a real point of confusion:
  // connected #contribute tabs only do work when the submitter has "Run on the mesh" on.
  const unit = mode === "mesh" ? "peers" : "cores";
  const title = mode === "mesh" ? "Browser tabs tiling the problem" : "Your CPU cores tiling the problem";

  container.innerHTML = `
    <div class="run-visual">
      <div class="rv-pane rv-grid-pane">
        <div class="rv-head">
          <span class="rv-title">${title}</span>
          <span class="rv-counts" data-counts>
            <span class="rv-count"><b data-workers>0</b> ${unit}</span>
            <span class="rv-count"><b data-inflight>0</b> computing</span>
          </span>
        </div>
        <canvas class="rv-grid" data-grid></canvas>
        <div class="rv-sub muted" data-grid-sub>waiting for first chunk…</div>
      </div>
      <div class="rv-pane rv-conv-pane">
        <div class="rv-head">
          <span class="rv-title">Running estimate <span class="rv-preview-tag">live preview</span></span>
          <span class="rv-estimate" data-estimate>—</span>
        </div>
        <div class="rv-chart" data-chart></div>
        <div class="rv-sub muted">${escapeHtml(resultUnit)} · ±95% band narrows as N grows · final result re-reduces canonically</div>
      </div>
    </div>
  `;

  const gridCanvas = container.querySelector<HTMLCanvasElement>("[data-grid]")!;
  const gridSub = container.querySelector<HTMLElement>("[data-grid-sub]")!;
  const workersEl = container.querySelector<HTMLElement>("[data-workers]")!;
  const inflightEl = container.querySelector<HTMLElement>("[data-inflight]")!;
  const estimateEl = container.querySelector<HTMLElement>("[data-estimate]")!;
  const chartEl = container.querySelector<HTMLElement>("[data-chart]")!;

  // ── Materialise grid state (sized on the first chunk) ──
  let numChunks = 0;
  let cellWorker = new Int32Array(0); // -1 = pending
  const seenWorkers = new Set<number>();
  let landed = 0;
  // "Currently computing" = chunks dispatched but not yet returned, bounded by how many
  // distinct workers we've seen. A worker holds ~one chunk at a time, so active-workers is a
  // faithful live concurrency readout; we cap it by chunks still outstanding near the end.
  const lastWorkerSeenAt = new Map<number, number>();

  // ── Convergence state: count-weighted running mean + running variance of chunk samples.
  // Welford-style accumulation over chunk samples (advisory; preview only). We also track a
  // simple SE on the chunk-sample mean for the ±band.
  let wSum = 0; // Σ count
  let wMean = 0; // running weighted mean of samples
  let m2 = 0; // weighted sum of squared deviations (for variance of chunk samples)
  let nSamples = 0;
  const xs: number[] = []; // cumulative iterations at each chunk arrival
  const ys: number[] = []; // running estimate
  const lo: number[] = []; // estimate − 1.96·SE
  const hi: number[] = []; // estimate + 1.96·SE

  // ── uPlot setup ──
  let plot: uPlot | null = null;
  let chartW = chartEl.clientWidth || 420;
  const chartH = 200;

  function buildPlot() {
    const opts: uPlot.Options = {
      width: chartW,
      height: chartH,
      cursor: { show: true, x: true, y: true },
      legend: { show: false },
      scales: { x: { time: false } },
      axes: [
        {
          stroke: "rgba(30,31,31,0.5)",
          grid: { stroke: "rgba(30,31,31,0.07)", width: 1 },
          ticks: { stroke: "rgba(30,31,31,0.12)" },
          font: "10px ui-monospace, SFMono-Regular, Menlo, monospace",
          values: (_u, vals) => vals.map((v) => compactInt(v)),
        },
        {
          stroke: "rgba(30,31,31,0.5)",
          grid: { stroke: "rgba(30,31,31,0.07)", width: 1 },
          ticks: { stroke: "rgba(30,31,31,0.12)" },
          font: "10px ui-monospace, SFMono-Regular, Menlo, monospace",
          size: 52,
        },
      ],
      series: [
        {},
        // Lower CI bound (drawn faint; the band is the fill between lo and hi via the band API).
        { stroke: "transparent", points: { show: false }, value: (_u, v) => fmt(v) },
        { stroke: "transparent", points: { show: false }, value: (_u, v) => fmt(v) },
        // The running estimate line (brand teal — this is the headline series).
        {
          stroke: "#51c0a7",
          width: 2,
          points: { show: false },
          value: (_u, v) => fmt(v),
        },
      ],
      bands: [{ series: [2, 1], fill: "rgba(81,192,167,0.12)" }],
    };
    // Data layout: [x, lo, hi, estimate]
    plot = new uPlot(opts, [[], [], [], []], chartEl);
  }
  buildPlot();

  // Redraw the grid (cheap; called per chunk). Cells coloured by workerId via the shared
  // materialise-grid component, reused on the #contribute page.
  function drawGrid() {
    if (numChunks <= 0) return;
    drawCellGrid(gridCanvas, cellWorker);
  }
  drawGrid();

  // Live counts that replace the (unbounded) per-worker legend: distinct contributing workers
  // and how many are computing right now. "Computing now" = distinct workers that returned a
  // chunk within a short recency window (each holds ~one chunk while busy), capped by chunks
  // still outstanding so it falls to 0 as the run drains. Recomputed both on each chunk and on
  // a steady timer (below) so the readout decays smoothly between events rather than freezing
  // on the last value — important for fast kernels where chunks land in bursts.
  const ACTIVE_WINDOW_MS = 1200;
  function updateCounts(now: number) {
    workersEl.textContent = String(seenWorkers.size);
    const remaining = numChunks - landed;
    if (numChunks > 0 && remaining <= 0) {
      inflightEl.textContent = "0"; // run complete — nothing computing
      return;
    }
    let active = 0;
    for (const t of lastWorkerSeenAt.values()) if (now - t <= ACTIVE_WINDOW_MS) active++;
    inflightEl.textContent = String(Math.max(0, Math.min(active, remaining || active)));
  }
  const countsTimer = setInterval(() => updateCounts(performance.now()), 250);

  function pushPoint() {
    plot?.setData([xs, lo, hi, ys]);
  }

  function onChunk(c: ChunkEvent): void {
    // First event sizes the grid (numChunks is a fixed function of (kernel, N)).
    if (numChunks === 0 && c.numChunks > 0) {
      numChunks = c.numChunks;
      cellWorker = new Int32Array(numChunks).fill(CELL_PENDING);
    }
    const known = c.workerId >= 0;
    // ── grid ──
    if (c.chunkIndex >= 0 && c.chunkIndex < numChunks && cellWorker[c.chunkIndex] === CELL_PENDING) {
      // Colour by real worker when we know it; neutral "done" tint otherwise (e.g. a mesh run
      // against an older coordinator that doesn't stamp a peer id).
      cellWorker[c.chunkIndex] = known ? c.workerId : CELL_DONE_NEUTRAL;
      landed++;
    }
    const now = performance.now();
    if (known) {
      seenWorkers.add(c.workerId);
      lastWorkerSeenAt.set(c.workerId, now);
    }
    drawGrid();
    updateCounts(now);
    const workerNote = seenWorkers.size > 0
      ? ` · ${seenWorkers.size} ${unit === "peers" ? (seenWorkers.size === 1 ? "peer" : "peers") : (seenWorkers.size === 1 ? "core" : "cores")}`
      : "";
    gridSub.textContent = `${landed.toLocaleString()} / ${numChunks.toLocaleString()} chunks done${workerNote}`;

    // ── convergence (count-weighted Welford) ──
    if (Number.isFinite(c.sample)) {
      const w = Math.max(1, c.count);
      const newWSum = wSum + w;
      const delta = c.sample - wMean;
      wMean += (w / newWSum) * delta;
      m2 += w * delta * (c.sample - wMean);
      wSum = newWSum;
      nSamples++;
      // SE of the (weighted) mean of chunk estimates — preview only.
      const variance = wSum > 0 ? m2 / wSum : 0;
      const se = nSamples > 1 ? Math.sqrt(variance / nSamples) : 0;
      const band = 1.96 * se;
      xs.push(c.completed);
      ys.push(wMean);
      lo.push(wMean - band);
      hi.push(wMean + band);
      pushPoint();
      estimateEl.textContent = fmt(wMean) + (band > 0 ? ` ± ${fmt(band, 3)}` : "");
    }
  }

  // Keep the chart crisp on resize.
  const ro = new ResizeObserver(() => {
    const w = chartEl.clientWidth;
    if (w && w !== chartW) {
      chartW = w;
      plot?.setSize({ width: chartW, height: chartH });
    }
    drawGrid();
  });
  ro.observe(chartEl);
  ro.observe(gridCanvas);

  function dispose() {
    clearInterval(countsTimer);
    ro.disconnect();
    plot?.destroy();
    plot = null;
  }

  return { onChunk, dispose };
}

// ── formatting helpers (mirror results.ts so the live + final readouts match) ──
function fmt(v: number, p = 6): string {
  if (!Number.isFinite(v)) return "—";
  return Math.abs(v) >= 1e6 || (v !== 0 && Math.abs(v) < 1e-4)
    ? v.toExponential(4)
    : v.toPrecision(p);
}

function compactInt(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(v % 1e3 === 0 ? 0 : 1) + "k";
  return String(Math.round(v));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
